import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pagination } from "antd";
import NewsCard from "../../components/_shared/news-card/NewsCard";
import useFetch from "../../hooks/useFetch";
import { useLocation, useNavigate } from "react-router-dom";
import { PaginatedNewsResponse } from "../../types/types";
import styles from "./News.module.scss";
import { replaceStringSpaces } from "../../utils/utils";
import DataNotFound from "../../components/_shared/data-not-found/DataNotFound";
import Spinner from "../../components/_shared/loader/Spinner";

// Moved outside component — static data, no reason to recreate on every render
const SPECIAL_CATEGORIES = [
  { id: "f81c4fea-504a-4ef3-8871-c9c89a757c3a", name: "KRYESORE" },
  { id: "1834aec1-fa4a-4c19-b133-79e19ff7585e", name: "VENDIMET" },
  { id: "78679015-b818-47d0-b3be-14781552d2fa", name: "TË TJERA" },
];

const isSpecialCategoryId = (id?: string): boolean =>
    SPECIAL_CATEGORIES.some((category) => category.id === id);

const News = () => {
  const { t, i18n } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();

  const subCategoryId = state?.subCategory?.id as string | undefined;
  const categoryId = state?.category?.id as string | undefined;

  // Stable derived value — no stale closure issues
  const isSpecialCategory = isSpecialCategoryId(subCategoryId);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(
      isSpecialCategory ? (subCategoryId ?? null) : null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Sync state when location/category changes
  useEffect(() => {
    setSelectedCategory(isSpecialCategoryId(subCategoryId) ? (subCategoryId ?? null) : null);
    setCurrentPage(1);
  }, [subCategoryId]);

  const childSubcategoryId = isSpecialCategory
      ? selectedCategory
      : subCategoryId;

  const endpoint = `/posts/published?subCategoryId=${categoryId}&childSubcategoryId=${childSubcategoryId}&page=${currentPage - 1}&size=${pageSize}`;

  const { data, loading } = useFetch<PaginatedNewsResponse>(endpoint);

  const mainNews = useMemo(() => {
    if (!data || currentPage !== 1) return null;
    return data.content[0] ?? null;
  }, [data, currentPage]);

  const paginatedNews = useMemo(() => {
    if (!data) return [];
    return currentPage === 1 ? data.content.slice(1) : data.content;
  }, [data, currentPage]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  }, []);

  const handleMainNewsNavigation = useCallback(() => {
    if (!mainNews) return;
    const title = i18n.language === "al" ? mainNews.alTitle : mainNews.enTitle;
    const slugifiedTitle = replaceStringSpaces(title ?? "");
    navigate(`/lajmet/${slugifiedTitle}`, { state: { ...mainNews } });
  }, [navigate, mainNews, i18n]);

  const handleCategoryChange = useCallback((id: string) => {
    setSelectedCategory(id);
    setCurrentPage(1);
  }, []);

  const getTitle = (item: typeof mainNews) => {
    if (!item) return "";
    return i18n.language === "al" ? item.alTitle : item.enTitle;
  };

  // Key forces full remount (including images) when category changes
  const containerKey = `${subCategoryId ?? ""}-${selectedCategory ?? ""}`;

  return (
      <div className={styles.NewsContainer} key={containerKey}>
        <h2>{t("app.news.news", "Informacionet më të reja")}</h2>

        {isSpecialCategory && (
            <div className={styles.ButtonContainer}>
              {SPECIAL_CATEGORIES.map((category) => (
                  <button
                      key={category.id}
                      onClick={() => handleCategoryChange(category.id)}
                      className={`${styles.CategoryButton} ${
                          selectedCategory === category.id ? styles.Active : ""
                      }`}
                  >
                    {category.name}
                  </button>
              ))}
            </div>
        )}

        {loading ? (
            <Spinner />
        ) : data && data.content.length > 0 ? (
            <>
              {mainNews && (
                  <div className={styles.News} onClick={handleMainNewsNavigation}>
                    <img src={mainNews.mainImageUrl} alt={getTitle(mainNews)} />
                    <div className={styles.MainNews}>
                      <h1>{getTitle(mainNews)}</h1>
                    </div>
                  </div>
              )}

              <h1>{t("app.news.main", "Kryesore")}</h1>

              <div className={styles.AllNews}>
                {paginatedNews.map((newsItem) => (
                    <NewsCard key={newsItem.id} {...newsItem} />
                ))}
              </div>

              <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={data.totalElements ?? 0}
                  onChange={handlePageChange}
                  onShowSizeChange={handlePageSizeChange}
                  showSizeChanger
                  className={styles.Pagination}
              />
            </>
        ) : (
            <DataNotFound loading={loading} />
        )}
      </div>
  );
};

export default News;